import { defaultRecipe } from '@lumos/core';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { GroupLabel, Panel } from '../controls/Panel.tsx';
import { Slider } from '../controls/Slider.tsx';

const DEFAULTS = defaultRecipe();

export function EffectsPanel() {
  const photo = useSelectedPhoto();
  const updateRecipe = useStore((state) => state.updateRecipe);
  const commit = useStore((state) => state.commit);

  if (!photo) return null;
  const recipe = photo.recipe;

  const active =
    recipe.texture !== DEFAULTS.texture ||
    recipe.clarity !== DEFAULTS.clarity ||
    recipe.dehaze !== DEFAULTS.dehaze ||
    recipe.vignetteAmount !== DEFAULTS.vignetteAmount ||
    recipe.vignetteMidpoint !== DEFAULTS.vignetteMidpoint ||
    recipe.vignetteFeather !== DEFAULTS.vignetteFeather ||
    recipe.vignetteRoundness !== DEFAULTS.vignetteRoundness ||
    recipe.grainAmount !== DEFAULTS.grainAmount ||
    recipe.grainSize !== DEFAULTS.grainSize;

  return (
    <Panel id="effects" title="Effects" active={active}>
      <GroupLabel>Presence</GroupLabel>
      <Slider
        label="Texture"
        value={recipe.texture}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Fine local contrast: pores, fabric, foliage. Negative softens skin and leaves the edges alone."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.texture = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Clarity"
        value={recipe.clarity}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Mid-size local contrast. Adds punch, but hardens skin if you push it."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.clarity = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Dehaze"
        value={recipe.dehaze}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Cuts the flat veil distance leaves behind. High values push saturation and block up shadows."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.dehaze = value;
          })
        }
        onCommit={commit}
      />

      <GroupLabel>Vignette</GroupLabel>
      <Slider
        label="Amount"
        value={recipe.vignetteAmount}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Darkens or brightens the edges to hold the eye in the middle. Around -20 reads as intent."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.vignetteAmount = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Midpoint"
        value={recipe.vignetteMidpoint}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.vignetteMidpoint}
        help="How far in from the corners the vignette reaches."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.vignetteMidpoint = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Feather"
        value={recipe.vignetteFeather}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.vignetteFeather}
        help="Softness of the transition. A high value is why a good vignette goes unnoticed."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.vignetteFeather = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Roundness"
        value={recipe.vignetteRoundness}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Shape of the falloff, from a rectangle that follows the frame to a circle."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.vignetteRoundness = value;
          })
        }
        onCommit={commit}
      />

      <GroupLabel>Grain</GroupLabel>
      <Slider
        label="Amount"
        value={recipe.grainAmount}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.grainAmount}
        help="Film-like noise. A little gives a clean file some texture and hides banding in skies."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.grainAmount = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Size"
        value={recipe.grainSize}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.grainSize}
        help="Bigger grain reads as faster film. Check it at 1:1, since it vanishes in a fit view."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.grainSize = value;
          })
        }
        onCommit={commit}
      />
    </Panel>
  );
}
